// src/pages/MergerDashboard.js

import React, { useEffect, useState, useMemo, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import ComparisonHeader from './ComparisonHeader';
import tileInformation from '../utils/tileInformation.json';
import BenefitBuildingsTab from './MergerDashboard/BenefitBuildingsTab'; // NEW
import AllianceTechnologyTab from './MergerDashboard/AllianceTechnologyTab';
import AllianceInstituteTab from './MergerDashboard/AllianceInstituteTab';
import PlayerSummaryTab from './MergerDashboard/PlayerSummaryTab';
import ConsolidationPlannerTab from './MergerDashboard/ConsolidationPlannerTab';
import "./css/MergerDashboard.css"
import HasPermission from '../utils/HasPermission';
import { AdminTabs, AppButton, PageShell } from '../components/ui';

const apiUrl = API_URL;

const dedupeBenefitTiles = (tiles = []) => {
  const seen = new Set();
  const deduped = [];

  tiles.forEach((tile) => {
    const key = [
      tile?.x,
      tile?.y,
      tile?.type,
      tile?.additionalTileInfo?.benefitSubType || 'unknown'
    ].join(':');
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    deduped.push(tile);
  });

  return deduped;
};

const summarizeBenefitTiles = (tiles = []) => {
  const summary = {};
  tiles.forEach((tile) => {
    const subType = tile?.additionalTileInfo?.benefitSubType || 'unknown';
    summary[subType] = (summary[subType] || 0) + 1;
  });
  return summary;
};

function MergerDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const axios = axiosInstance;
  const isSuperUser = user?.role === 'superUser';

  // Check if super user
  const canViewMergerDashboardUser = HasPermission("readMergerDashboard")

  // Lock comparison type to "nation"
  const [comparisonType, setComparisonType] = useState('nation');

  // We'll treat these as "nationA" and "nationB" IDs from ComparisonHeader
  const [nationA, setNationA] = useState('');
  const [nationB, setNationB] = useState('');

  // Loading state
  const [isLoadingTiles, setIsLoadingTiles] = useState(false);

  // Store fetched benefit tiles, summary info, and nation info
  const [benefitTiles, setBenefitTiles] = useState({ nationA: [], nationB: [] });
  const [summaryInformation, setSummaryInformation] = useState({
    nationA: {},
    nationB: {}
  });
  const [nationInfo, setNationInfo] = useState({
    nationA: { number: '' },
    nationB: { number: '' }
  });

  // NEW: Sub-menu tab state
  const [activeTab, setActiveTab] = useState('playerSummary');
  // Could be "playerSummary", "benefit", or "tech"
  const [activeNationForMerge, setActiveNationForMerge] = useState('');
  const [isExecutingMerge, setIsExecutingMerge] = useState(false);
  const [mergeExecutionResult, setMergeExecutionResult] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [planningSession, setPlanningSession] = useState(null);
  const [planningSessions, setPlanningSessions] = useState([]);
  const [isCreatingPlanningSession, setIsCreatingPlanningSession] = useState(false);
  const [isCancellingPlanningSession, setIsCancellingPlanningSession] = useState(false);
  const [cancellingSessionId, setCancellingSessionId] = useState('');
  const [isLoadingPlanningSessions, setIsLoadingPlanningSessions] = useState(false);
  const [isAuditingMerge, setIsAuditingMerge] = useState(false);
  const [mergeAuditResult, setMergeAuditResult] = useState(null);

  useEffect(() => {
    if (!isSuperUser && activeTab === 'consolidationPlanner') {
      setActiveTab('playerSummary');
    }
  }, [activeTab, isSuperUser]);

  // Redirect if not super user
  useEffect(() => {
    if (!canViewMergerDashboardUser) {
      navigate('/home');
    }
  }, [canViewMergerDashboardUser, navigate]);

  // Resolve merge scope for non-super users (self nation or active planning pair).
  useEffect(() => {
    if (!user || isSuperUser) return;

    const fetchScope = async () => {
      try {
        const response = await axios.get(`${apiUrl}/merger/planning-session`);
        const session = response.data?.session || null;
        setPlanningSession(session);

        if (session?.nations?.length === 2) {
          setNationA(session.nations[0]._id);
          setNationB(session.nations[1]._id);
          setActiveNationForMerge(session.activeNation?._id || session.nations[0]._id);
        } else {
          setNationA(typeof user.nation === 'string' ? user.nation : user.nation?._id);
          setNationB('');
        }
      } catch (error) {
        console.error('Error fetching non-super planning scope:', error);
        setNationA(typeof user.nation === 'string' ? user.nation : user.nation?._id);
        setNationB('');
      }
    };

    fetchScope();
  }, [user, isSuperUser, axios]);

  // Fetch benefit tiles once nationA is selected. nationB is optional (placeholder mode).
  useEffect(() => {
    if (!nationA) return;

    async function fetchBenefitTiles() {
      setIsLoadingTiles(true);
      try {
        const response = await axios.get(`${apiUrl}/merger/benefit-tiles`, {
          params: nationB ? { nationA, nationB } : { nationA }
        });
        const nationATiles = dedupeBenefitTiles(response?.data?.nationA || []);
        const nationBTiles = dedupeBenefitTiles(response?.data?.nationB || []);
        setBenefitTiles({
          nationA: nationATiles,
          nationB: nationBTiles
        });
        setSummaryInformation({
          nationA: summarizeBenefitTiles(nationATiles),
          nationB: summarizeBenefitTiles(nationBTiles)
        });
        setNationInfo(response.data.nationInfo || {
          nationA: { number: '' },
          nationB: { number: '' }
        });
      } catch (error) {
        console.error('Error fetching benefit tiles:', error);
      } finally {
        setIsLoadingTiles(false);
      }
    }

    fetchBenefitTiles();
  }, [nationA, nationB, axios]);

  useEffect(() => {
    if (!isSuperUser) return;
    if (nationA && nationB) {
      setActiveNationForMerge(nationA);
      setMergeExecutionResult(null);
      setMergeAuditResult(null);
    } else {
      setActiveNationForMerge('');
      setMergeAuditResult(null);
    }
  }, [nationA, nationB, isSuperUser]);

  useEffect(() => {
    setMergeAuditResult(null);
  }, [activeNationForMerge]);

  useEffect(() => {
    if (!nationA) {
      setComparisonData(null);
      return;
    }

    const fetchComparison = async () => {
      try {
        const response = await axios.get(`${apiUrl}/merger/player-summary`, {
          params: nationB ? { nationIds: [nationA, nationB] } : { nationIds: [nationA] }
        });
        if (!nationB) {
          const singleNationData = response.data || {};
          const knownNation = singleNationData.nations?.[0];
          setComparisonData({
            ...singleNationData,
            nations: [
              knownNation,
              { id: 'unknown-opponent', name: 'Unknown Opponent', number: 'TBD' }
            ],
            totals: {
              ...(singleNationData.totals || {}),
              battlePower: {
                ...(singleNationData.totals?.battlePower || {}),
                'unknown-opponent': 0
              },
              officerPower: {
                ...(singleNationData.totals?.officerPower || {}),
                'unknown-opponent': 0
              },
              titanPower: {
                ...(singleNationData.totals?.titanPower || {}),
                'unknown-opponent': 0
              },
              warplanePower: {
                ...(singleNationData.totals?.warplanePower || {}),
                'unknown-opponent': 0
              },
              islandScore: {
                ...(singleNationData.totals?.islandScore || {}),
                'unknown-opponent': 0
              },
              antiquesScore: {
                ...(singleNationData.totals?.antiquesScore || {}),
                'unknown-opponent': 0
              },
              totalMerit: {
                ...(singleNationData.totals?.totalMerit || {}),
                'unknown-opponent': 0
              }
            }
          });
        } else {
          setComparisonData(response.data);
        }
      } catch (error) {
        console.error('Error fetching merger comparison data:', error);
        setComparisonData(null);
      }
    };

    fetchComparison();
  }, [nationA, nationB, axios]);

  const loadSuperPlanningSessions = useCallback(async ({ showLoading = false } = {}) => {
    if (!isSuperUser) return;

    try {
      if (showLoading) setIsLoadingPlanningSessions(true);
      const response = await axios.get(`${apiUrl}/merger/planning-session`);
      const sessions = Array.isArray(response.data?.sessions) ? response.data.sessions : [];
      setPlanningSessions(sessions);

      if (nationA && nationB) {
        const selectedIds = [String(nationA), String(nationB)].sort();
        const match = sessions.find((session) => {
          const ids = (session?.nations || []).map((nation) => String(nation?._id || nation || '')).sort();
          return ids.length === 2 && ids[0] === selectedIds[0] && ids[1] === selectedIds[1];
        });
        setPlanningSession(match || null);
      } else {
        setPlanningSession((prev) => {
          if (!prev?._id) return null;
          const refreshed = sessions.find((session) => String(session?._id || '') === String(prev._id));
          return refreshed || null;
        });
      }
    } catch (error) {
      console.error('Error fetching planning sessions:', error);
    } finally {
      if (showLoading) setIsLoadingPlanningSessions(false);
    }
  }, [axios, isSuperUser, nationA, nationB]);

  useEffect(() => {
    if (!isSuperUser) return;
    loadSuperPlanningSessions({ showLoading: true });
  }, [nationA, nationB, isSuperUser, loadSuperPlanningSessions]);

  useEffect(() => {
    if (!isSuperUser || activeTab !== 'manageMergers') return;
    loadSuperPlanningSessions({ showLoading: true });
  }, [activeTab, isSuperUser, loadSuperPlanningSessions]);

  // Build chart data for Recharts
  const chartData = useMemo(() => {
    const subTypesA = summaryInformation.nationA || {};
    const subTypesB = summaryInformation.nationB || {};

    // Collect all unique subTypes
    const allSubTypes = new Set([
      ...Object.keys(subTypesA),
      ...Object.keys(subTypesB)
    ]);

    return Array.from(allSubTypes).map((subType) => ({
      subType,
      nationA: subTypesA[subType] || 0,
      nationB: subTypesB[subType] || 0
    }));
  }, [summaryInformation]);

  // Helper to get a friendly display name for each tile
  const getTileDisplayName = (tile) => {
    const typeKey = tile.type;
    const typeInfo = tileInformation[typeKey];
    let displayName = typeInfo?.niceName || typeKey;

    if (tile.additionalTileInfo?.benefitSubType && typeInfo?.subTypes) {
      const subTypeKey = tile.additionalTileInfo.benefitSubType;
      const subTypeLabel = typeInfo.subTypes[subTypeKey];
      if (subTypeLabel) {
        displayName = subTypeLabel;
      }
    }

    return displayName;
  };

  // If not super user, don't render the UI (hooks have already run)
  if (!canViewMergerDashboardUser) {
    return null;
  }

  const activeNationMeta = activeNationForMerge === nationA ? nationInfo.nationA : nationInfo.nationB;
  const absorbedNationId = activeNationForMerge === nationA ? nationB : nationA;
  const absorbedNationMeta = activeNationForMerge === nationA ? nationInfo.nationB : nationInfo.nationA;
  const mergeAuditCollections = mergeAuditResult?.counts?.collections || {};
  const mergeAuditBlockers = Array.isArray(mergeAuditResult?.blockers) ? mergeAuditResult.blockers : [];

  const runMergeAudit = async () => {
    if (!nationA || !nationB || !activeNationForMerge) return null;

    try {
      setIsAuditingMerge(true);
      const response = await axios.post(`${apiUrl}/merger/execute-audit`, {
        activeNationId: activeNationForMerge,
        absorbedNationId
      });
      const payload = response?.data || null;
      setMergeAuditResult(payload);
      return payload;
    } catch (error) {
      console.error('Error running merge audit:', error);
      const fallback = {
        ok: false,
        blockers: [{
          code: 'audit_request_failed',
          message: error?.response?.data?.message || 'Failed to run merge audit.'
        }]
      };
      setMergeAuditResult(fallback);
      window.alert(fallback.blockers[0].message);
      return fallback;
    } finally {
      setIsAuditingMerge(false);
    }
  };

  const executeMerge = async () => {
    if (!nationA || !nationB || !activeNationForMerge) return;

    const auditResult = await runMergeAudit();
    if (!auditResult || auditResult.ok !== true) {
      window.alert('Merge audit reported blockers. Resolve blockers before execution.');
      return;
    }

    const activeNationNumber = activeNationMeta?.number || 'Unknown';
    const absorbedNationNumber = absorbedNationMeta?.number || 'Unknown';

    const confirmed = window.confirm(
      `Execute merge now?\n\nActive Nation: ${activeNationNumber}\nAbsorbed Nation: ${absorbedNationNumber}\n\nThis operation is permanent.`
    );
    if (!confirmed) return;

    try {
      setIsExecutingMerge(true);
      const response = await axios.post(`${apiUrl}/merger/execute`, {
        activeNationId: activeNationForMerge,
        absorbedNationId,
        planningSessionId: planningSession?._id || null
      });
      setMergeExecutionResult(response.data);
      setPlanningSession((prev) => prev ? { ...prev, status: 'executed' } : prev);
      await loadSuperPlanningSessions();
    } catch (error) {
      console.error('Error executing merge:', error);
      window.alert(error?.response?.data?.message || 'Failed to execute merge.');
    } finally {
      setIsExecutingMerge(false);
    }
  };

  const createPlanningSession = async () => {
    if (!nationA || !nationB || !activeNationForMerge) return;
    try {
      setIsCreatingPlanningSession(true);
      const response = await axios.post(`${apiUrl}/merger/planning-session`, {
        nationA,
        nationB,
        activeNationId: activeNationForMerge
      });
      setPlanningSession(response.data);
      await loadSuperPlanningSessions();
    } catch (error) {
      console.error('Error creating planning session:', error);
      window.alert(error?.response?.data?.message || 'Failed to create planning session.');
    } finally {
      setIsCreatingPlanningSession(false);
    }
  };

  const cancelPlanningSessionById = async (sessionId) => {
    if (!sessionId) return;
    const confirmed = window.confirm('Cancel this pre-merge planning session?');
    if (!confirmed) return;

    try {
      setIsCancellingPlanningSession(true);
      setCancellingSessionId(String(sessionId));
      const response = await axios.post(
        `${apiUrl}/merger/planning-session/${sessionId}/cancel`
      );
      const cancelledSession = response?.data?.session;
      if (cancelledSession) {
        setPlanningSession((prev) => (
          String(prev?._id || '') === String(cancelledSession?._id || '')
            ? cancelledSession
            : prev
        ));
      }
      await loadSuperPlanningSessions();
    } catch (error) {
      console.error('Error cancelling planning session:', error);
      window.alert(error?.response?.data?.message || 'Failed to cancel planning session.');
    } finally {
      setIsCancellingPlanningSession(false);
      setCancellingSessionId('');
    }
  };

  const cancelPlanningSession = async () => {
    if (!planningSession?._id) return;
    await cancelPlanningSessionById(planningSession._id);
  };

  const openPlanningSessionPair = (session) => {
    const sessionNations = Array.isArray(session?.nations) ? session.nations : [];
    if (sessionNations.length !== 2) return;
    setNationA(String(sessionNations[0]?._id || sessionNations[0] || ''));
    setNationB(String(sessionNations[1]?._id || sessionNations[1] || ''));
    setActiveNationForMerge(String(session?.activeNation?._id || session?.activeNation || sessionNations[0]?._id || ''));
    setActiveTab('playerSummary');
  };

  const mergerTabs = [
    { value: 'playerSummary', label: 'Player Summary' },
    { value: 'benefit', label: 'Benefit Building Summary' },
    { value: 'tech', label: 'Alliance Technology Summary' },
    { value: 'institute', label: 'Alliance Institute Summary' },
  ];
  if (isSuperUser) {
    mergerTabs.splice(1, 0, { value: 'consolidationPlanner', label: 'Consolidation Planner' });
    mergerTabs.push({ value: 'manageMergers', label: 'Manage Mergers' });
  }

  return (
    <PageShell maxWidth={false} containerSx={{ px: { xs: 1, md: 2 }, maxWidth: "none" }}>
    <div className="merger-dashboard-page">
      {/* Super users can explore potential pairings. Others are auto-scoped. */}
      {isSuperUser ? (
        <ComparisonHeader
          comparisonType={comparisonType}
          setComparisonType={setComparisonType}
          setItem1={setNationA}
          setItem2={setNationB}
          hideComparisonTypeDropdown={true}
          item1Label="Select Nation A"
          item2Label="Select Nation B"
          selectPlaceholder="Select Nation"
        />
      ) : (
        <div className="md-scope-note">
          <p>
            Showing merger data for your nation.
            {nationB ? ' Pre-merge pairing is active.' : ' Pairing is not announced yet.'}
          </p>
        </div>
      )}

      <AdminTabs
        tabs={mergerTabs}
        value={activeTab}
        onChange={setActiveTab}
        className="md-sub-menu"
      />

      {/* Conditionally render the tab content */}
      {activeTab === 'playerSummary' && (
        <PlayerSummaryTab comparisonData={comparisonData} />
      )}

      {activeTab === 'benefit' && (
        <BenefitBuildingsTab
          nationA={nationA}
          nationB={nationB}
          nationInfo={nationInfo}
          isLoadingTiles={isLoadingTiles}
          chartData={chartData}
          benefitTiles={benefitTiles}
          getTileDisplayName={getTileDisplayName}
        />
      )}

      {isSuperUser && activeTab === 'consolidationPlanner' && (
        <ConsolidationPlannerTab
          nationA={nationA}
          nationB={nationB}
        />
      )}

      {activeTab === 'tech' && (
        <AllianceTechnologyTab 
        nationA={nationA} 
        nationB={nationB} />
      )}

      {activeTab === 'institute' && (
        <AllianceInstituteTab
          nationA={nationA}
          nationB={nationB}
        />
      )}

      {isSuperUser && activeTab === 'manageMergers' && (
        <div className="md-card">
          <h2>Active Planning Mergers</h2>
          <p>All active pre-merge planning sessions across nations.</p>
          <div className="md-planning-actions">
            <AppButton
              className="md-secondary-button"
              onClick={() => loadSuperPlanningSessions({ showLoading: true })}
              disabled={isLoadingPlanningSessions || isCancellingPlanningSession}
            >
              {isLoadingPlanningSessions ? 'Refreshing...' : 'Refresh List'}
            </AppButton>
          </div>
          {planningSessions.length === 0 ? (
            <p className="md-session-status">No active planning mergers found.</p>
          ) : (
            <div className="md-table-wrap">
              <table className="md-summary-table">
                <thead>
                  <tr>
                    <th>Nation Pair</th>
                    <th>Active Nation</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {planningSessions.map((session) => {
                    const nations = Array.isArray(session?.nations) ? session.nations : [];
                    const pairLabel = nations
                      .map((nation) => `${nation?.number ?? '?'}`)
                      .join(' + ');
                    const activeNationLabel = `${session?.activeNation?.number ?? '?'}`;
                    const updatedLabel = session?.updatedAt
                      ? new Date(session.updatedAt).toLocaleString()
                      : 'Unknown';
                    const rowSessionId = String(session?._id || '');
                    const isCancellingRow = isCancellingPlanningSession && cancellingSessionId === rowSessionId;

                    return (
                      <tr key={rowSessionId}>
                        <td>{pairLabel || 'Unknown Pair'}</td>
                        <td>{activeNationLabel}</td>
                        <td>{updatedLabel}</td>
                        <td>
                          <div className="md-manage-actions">
                            <AppButton
                              className="md-secondary-button"
                              onClick={() => openPlanningSessionPair(session)}
                              disabled={isCancellingPlanningSession}
                            >
                              Open Pair
                            </AppButton>
                            <AppButton
                              className="md-danger-button"
                              onClick={() => cancelPlanningSessionById(rowSessionId)}
                              disabled={isCancellingPlanningSession || !rowSessionId}
                            >
                              {isCancellingRow ? 'Cancelling...' : 'Cancel'}
                            </AppButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isSuperUser && nationA && nationB && activeTab !== 'manageMergers' && (
        <div className="md-planning-section">
          <h2>Pre-Merge Planning Access</h2>
          <p>Enable planning mode so users from both nations can access both maps and cross-nation planning tools.</p>
          <div className="md-planning-actions">
            <AppButton
              className="md-primary-button"
              onClick={createPlanningSession}
              disabled={!activeNationForMerge || isCreatingPlanningSession || isCancellingPlanningSession}
            >
              {isCreatingPlanningSession ? 'Saving Planning Session...' : 'Enable Pre-Merge Planning'}
            </AppButton>
            {planningSession?.status === 'planning' && (
              <AppButton
                className="md-secondary-button"
                onClick={cancelPlanningSession}
                disabled={isCancellingPlanningSession || isCreatingPlanningSession}
              >
                {isCancellingPlanningSession ? 'Cancelling...' : 'Cancel Planning Session'}
              </AppButton>
            )}
          </div>
          {planningSession && (
            <p className="md-session-status">
              Planning session status: <strong>{planningSession.status}</strong>
            </p>
          )}

          <h2>Execute Nation Merge</h2>
          <p>Choose which selected nation remains active. The other nation will be marked inactive and absorbed.</p>

          <div className="md-radio-group">
            <label className="md-radio-option">
              <input
                type="radio"
                name="activeNationForMerge"
                value={nationA}
                checked={activeNationForMerge === nationA}
                onChange={() => setActiveNationForMerge(nationA)}
              />{' '}
              Keep Nation {nationInfo?.nationA?.number || 'A'} Active
            </label>
            <label className="md-radio-option">
              <input
                type="radio"
                name="activeNationForMerge"
                value={nationB}
                checked={activeNationForMerge === nationB}
                onChange={() => setActiveNationForMerge(nationB)}
              />{' '}
              Keep Nation {nationInfo?.nationB?.number || 'B'} Active
            </label>
          </div>

          <button
            className="md-danger-button"
            onClick={executeMerge}
            disabled={!activeNationForMerge || isExecutingMerge || isAuditingMerge}
          >
            {isExecutingMerge ? 'Executing Merge...' : 'Execute Merge'}
          </button>

          <div className="md-planning-actions" style={{ marginTop: '12px' }}>
            <AppButton
              className="md-secondary-button"
              onClick={runMergeAudit}
              disabled={!activeNationForMerge || isAuditingMerge || isExecutingMerge}
            >
              {isAuditingMerge ? 'Running Dry-Run Audit...' : 'Run Dry-Run Audit'}
            </AppButton>
          </div>

          {mergeAuditResult && (
            <div className="md-result-block">
              <p>
                Merge audit status: <strong>{mergeAuditResult.ok ? 'Ready to execute' : 'Blocked'}</strong>
              </p>
              {mergeAuditBlockers.length > 0 && (
                <>
                  <p>Blockers:</p>
                  <ul>
                    {mergeAuditBlockers.map((blocker, index) => (
                      <li key={`${blocker?.code || 'blocker'}-${index}`}>
                        {blocker?.message || 'Unknown blocker'}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <p>Projected moved records from absorbed nation:</p>
              <p>Users: {mergeAuditCollections?.users?.movedFromAbsorbed ?? 0}</p>
              <p>Commanders: {mergeAuditCollections?.commanders?.movedFromAbsorbed ?? 0}</p>
              <p>Commander history: {mergeAuditCollections?.commanderHistoryRecords?.movedFromAbsorbed ?? 0}</p>
              <p>Alliances: {mergeAuditCollections?.alliances?.movedFromAbsorbed ?? 0}</p>
              <p>Historical event scope retained in absorbed nation:</p>
              <p>Event cores retained: {mergeAuditCollections?.eventCores?.retainedHistoricalScopeFromAbsorbed ?? mergeAuditCollections?.eventCores?.absorbed ?? 0}</p>
              <p>Legacy events retained: {mergeAuditCollections?.legacyTriangleWarEvents?.retainedHistoricalScopeFromAbsorbed ?? mergeAuditCollections?.legacyTriangleWarEvents?.absorbed ?? 0}</p>
              <p>Event nation refs retained: {mergeAuditCollections?.eventResultNationRefs?.retainedHistoricalScopeFromAbsorbed ?? mergeAuditCollections?.eventResultNationRefs?.absorbed ?? 0}</p>
            </div>
          )}

          {mergeExecutionResult && (
            <div className="md-result-block">
              <p>{mergeExecutionResult.message}</p>
              <p>Users moved: {mergeExecutionResult.moved?.users ?? 0}</p>
              <p>Commanders moved: {mergeExecutionResult.moved?.commanders ?? 0}</p>
              <p>Commander history moved: {mergeExecutionResult.moved?.commanderHistoryRecords ?? 0}</p>
              <p>Alliances moved: {mergeExecutionResult.moved?.alliances ?? 0}</p>
              <p>Event cores retained historically: {mergeExecutionResult.retainedHistoricalScope?.eventCores ?? 0}</p>
              <p>Legacy events retained historically: {mergeExecutionResult.retainedHistoricalScope?.legacyEvents ?? 0}</p>
              <p>Event nation refs retained historically: {mergeExecutionResult.retainedHistoricalScope?.eventResultNationRefs ?? 0}</p>
              <p>Execution mode: {mergeExecutionResult.executionMode || 'unknown'}</p>
            </div>
          )}
        </div>
      )}
    </div>
    </PageShell>
  );
}

export default MergerDashboard;
